import type Store from '@ember-data/store';
import { rawTimeout, task } from 'ember-concurrency';
import type { FormatUrlParams } from 'frontend-utils/utils/url/format-url';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  ABSTRACT_ACTIONS,
  type AbstractActionValue,
} from 'lumen/utils/constants/restriction-actions';

interface RequestActionStatusesResponse<T> {
  actions?: T[];
  hasError?: boolean;
}

/** Definitions of configuration object to be used in the base-action-poller */
export interface ConfigObject<T> {
  actionModelName: string;
  interval: number;
  logBeforeStatusRequest?: (itemsIds: string) => void;
  logItemDeleted?: (actionId: string, itemsCount: number) => void;
  logPollingStarted?: () => void;
  logStatusRefresh?: (actionId: string) => void;
  logStatusRequestError?: (error: unknown) => void;
  onActionInEndState?: (action: string, item: T) => Promise<void>;
  onPollingFinished?: () => void;
  typeKey: string;
}

/** Represents the minimal action information */
export interface Action {
  action: AbstractActionValue;
  id: string | null;
}

/**
 * Class that controls a polling logic.
 *
 * In `pollRecommendationsTask` a simple loop is defined, where the `generalFlow` function is called every 2 seconds.
 * The loop stops once `recommendationsToFollow` is empty.
 *
 * The `generalFlow` executes the task `requestActionStatusesTask`, and checks the response. In case it
 * is PUSHED or EXCEPTION, the recommendation object is removed from `recommendationsToFollow`, and
 * callbacks are called.
 *
 * Recommended Example
 * ```typescript
 * import BaseActionPoller from 'lumen/services/base-action-poller';
 *
 * export default class RestrictionActionPollerService extends Service {
 *   declare baseActionPoller: BaseActionPoller<RestrictionAction>;
 *
 *   constructor(...args: ConstructorParameters<typeof Service>) {
 *     super(...args);
 *
 *     this.baseActionPoller = new BaseActionPoller<RestrictionAction>(
 *       {
 *         typeKey: 'pricing-assistant/restrictions/recommendations/action/status',
 *         actionModelName: 'restriction-action',
 *         interval: 2000,
 *         onActionInEndState: this.onActionInEndState.bind(this),
 *         onPollingFinished: this.onPollingFinished.bind(this),
 *       },
 *       this.store,
 *     );
 *     this.baseActionPoller.subscriptionId = this.subscriptionId;
 *   }
 * }
 * ```
 */
export default class BaseActionPoller<T extends Action> {
  actionsToFollow: Record<string, T> = {};
  configObj: ConfigObject<T>;
  POLLING_INTERVAL = 2000;
  declare store: Store;
  subscriptionId: number | null = null;

  pollLoopTask = task({ restartable: true }, async (): Promise<void> => {
    if (this.configObj?.logPollingStarted) {
      this.configObj.logPollingStarted();
    }

    while (Object.keys(this.actionsToFollow).length > 0) {
      await rawTimeout(this.configObj?.interval ?? this.POLLING_INTERVAL); // Wait 2 seconds before logging again
      await this.generalFlow();
    }
  });

  /**
   * Task that handles the request for the latest action statuses
   * Use the list of ids to query all the action statuses from API
   */
  requestActionStatusesTask = task(
    { restartable: true },
    async (subscriptionId: number, ids) => {
      if (!this.configObj?.typeKey || !this.configObj?.actionModelName) {
        throw new Error('BaseActionPoller url missing polling endpoint info');
      }

      try {
        const requestParams: FormatUrlParams = {
          subscription_id: subscriptionId,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          'ids[]': ids,
          namespace: APIGATEWAY,
          typeKey: this.configObj.typeKey,
        };

        if (this.configObj?.logBeforeStatusRequest) {
          this.configObj.logBeforeStatusRequest(
            Object.keys(this.actionsToFollow).join(' / ').toString(),
          );
        }

        const apiResponse = await this.store.query<T>(
          // @ts-expect-error: Argument of type 'string' is not assignable to parameter of type 'TypeFromInstance<T>'.
          this.configObj.actionModelName,
          requestParams,
        );

        const response: RequestActionStatusesResponse<T> = {
          actions: [...apiResponse],
          hasError: false,
        };

        return response;
      } catch (error) {
        if (this.configObj?.logStatusRequestError) {
          this.configObj.logStatusRequestError(error);
        }

        const response: RequestActionStatusesResponse<T> = {
          hasError: true,
        };
        return response;
      }
    },
  );

  constructor(configObj: ConfigObject<T>, store: Store) {
    this.configObj = configObj;
    this.store = store;
  }

  /**
   * Add a recommendation to the service array to follow up/poll for latest recommendationAction statuses
   */

  addActionsToFollow(actions: T[]): void {
    actions.forEach((action) => {
      if (action.id) {
        this.actionsToFollow[action.id] = action;
      }
    });

    if (this.pollLoopTask.isRunning) {
      return;
    }

    void this.pollLoopTask.perform();
  }

  async generalFlow(): Promise<void> {
    const actionIds = Object.values(this.actionsToFollow).map(
      (action) => action.id,
    );

    if (!this.subscriptionId) {
      throw Error('Unable to find subscription id');
    }

    const response = await this.requestActionStatusesTask.perform(
      this.subscriptionId,
      actionIds,
    );
    // Prepare responses & Loop
    // 1. Loop until at least one entry with non-end action state i.e pushed or exception
    // 2. Update / re-request data once the recommendation when it reach the end action state
    if (response && response.actions) {
      response.actions.forEach((item: T) => {
        if (!item.id) {
          return;
        }

        if (!this.actionsToFollow[item.id]) {
          return;
        }

        // Log Status refreshed
        if (this.configObj?.logStatusRefresh) {
          this.configObj.logStatusRefresh(item.id);
        }

        if (
          item.action === ABSTRACT_ACTIONS.PUSHED ||
          item.action === ABSTRACT_ACTIONS.EXCEPTION
        ) {
          // Remove recommendation that is in end state from our local array (we don't need to poll this anymore)
          this.removeActionToFollow(item);

          if (this.configObj?.onActionInEndState) {
            void this.configObj.onActionInEndState(item.action, item);
          }
        }
      });

      // All Actions tested
      if (this.configObj.onPollingFinished) {
        this.configObj.onPollingFinished();
      }
    }
  }

  /**
   * Removes an action from the poller array to follow up/poll for latest statuses
   */

  removeActionToFollow(action: T): void {
    if (!action.id) {
      return;
    }

    delete this.actionsToFollow[action.id];

    if (this.configObj?.logItemDeleted) {
      this.configObj.logItemDeleted(
        action.id,
        Object.keys(this.actionsToFollow).length,
      );
    }
  }

  stopPolling(): void {
    if (this.requestActionStatusesTask.isRunning) {
      void this.requestActionStatusesTask.cancelAll();
    }

    this.actionsToFollow = {};
  }
}
